import { ReactNode } from 'react';
import { Input } from '@mantine/core';
import clsx from 'clsx';

import { FormDescription } from '../formfield/FormDescription';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { Tooltip } from '../tooltip/Tooltip';

import styles from './form-label.module.scss';

export type FormLabelProps = {
  className?: string;
  description?: ReactNode | ReactNode[];
  disabled?: boolean;
  error?: string | boolean;
  /** id of associated input element to focus input on label click */
  id?: string;
  /** set to true, if label and error should be displayed inline */
  inline?: boolean;
  label?: ReactNode | ReactNode[];
  /** Material icon name for a badge shown after the label. */
  badgeIconName?: string;
  /** Tooltip text for `badgeIconName`. */
  badgeTooltip?: ReactNode;
  required?: boolean;
  loading?: boolean;
};

/**
 * label to be used in conjunction with an input element.
 * Returns undefined if label is empty
 */
export function FormLabel({
  className,
  description,
  id,
  label,
  badgeIconName,
  badgeTooltip,
  error,
  inline,
  disabled,
  loading,
  required,
}: FormLabelProps) {
  if (!label) {
    return;
  }
  const withError = error && error !== '';

  const formLabel = (
    <Input.Label
      htmlFor={id}
      required={required}
      data-testid="form-label"
      className={clsx(
        className,
        styles['label'],
        withError && styles['label--error'],
        disabled && styles['label--disabled'],
        loading && styles['label--loading'],
        !disabled && !loading && styles['label--enabled']
      )}
    >
      {label}
      {badgeIconName && (
        <Tooltip label={badgeTooltip} disabled={disabled || !badgeTooltip}>
          <GoogleFontIcon
            name={badgeIconName}
            badge
            aria-hidden="true"
            className={styles['badge']}
          />
        </Tooltip>
      )}
      {withError && inline && (
        <Tooltip label={error} usePortal={false} width="200px">
          <GoogleFontIcon name="report" className={styles['error-icon']} />
        </Tooltip>
      )}
    </Input.Label>
  );

  if (!description) {
    return formLabel;
  }

  return (
    <div
      data-testid="form-label-root"
      className={clsx(styles['root'], styles['label-with-description'])}
    >
      {formLabel}
      <FormDescription className={styles['description']}>
        {description}
      </FormDescription>
    </div>
  );
}
